import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ChevronRight, Radio, Coins, LockKeyhole, Archive } from "lucide-react";

const icons = [Radio, Coins, LockKeyhole, Archive];

export default function Landing() {
  const { t } = useI18n();
  return (
    <div className="stripe-bg grain relative">
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-20 pb-24 lg:pt-32 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10 animate-float-up">
            <p className="text-sm uppercase tracking-[0.3em] text-primary/80 mb-6" data-testid="hero-tag">{t.tagline}</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              {t.hero_title.split(" ").slice(0, -3).join(" ")}{" "}
              <span className="gold-text">{t.hero_title.split(" ").slice(-3).join(" ")}</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              {t.hero_sub}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full h-12 ps-5 pe-6 gap-2 shadow-lg shadow-primary/20" data-testid="cta-watch-demo">
                <Link to="/demo">
                  <span className="w-6 h-6 rounded-full grid place-items-center bg-red-500/20 ring-1 ring-red-500/50">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                  </span>
                  {t.watch_demo || "Canlı demoyu izle"}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-6 border-primary/40" data-testid="cta-start">
                <Link to="/register">{t.cta_start}</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="rounded-full h-12 px-4" data-testid="cta-jeweler-demo">
                <Link to="/demo/jeweler">Kuyumcu paneli</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="rounded-full h-12 px-4" data-testid="cta-memory-demo">
                <Link to="/demo/memory">Anı defteri</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" className="rounded-full h-12 px-4" data-testid="cta-guest">
                <Link to="/g">{t.cta_guest}</Link>
              </Button>
            </div>
            <div className="mt-8 flex items-center gap-4 text-xs uppercase tracking-widest text-muted-foreground">
              <span className="inline-block w-8 h-px bg-primary/40" /> {t.demo_hint || "Kayıt olmadan tam demoyu deneyin"}
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-primary/20">
              <div className="absolute inset-0 bg-secondary/60 z-10 mix-blend-multiply" />
              <img alt="event" className="absolute inset-0 w-full h-full object-cover"
                src="https://images.unsplash.com/photo-1519167758481-83f550bb49b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBlbGVnYW50JTIwZXZlbnQlMjBzZXR1cHxlbnwwfHx8fDE3ODY2MjEyMzF8MA&ixlib=rb-4.1.0&q=85" />
              <div className="absolute inset-x-0 bottom-0 z-20 p-6 text-white">
                <div className="text-xs uppercase tracking-[0.3em] opacity-70">Live · İstanbul</div>
                <div className="font-serif text-3xl mt-2">Ayşe & Mehmet</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-24">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-serif text-3xl sm:text-4xl">{t.features}</h2>
          <div className="hidden sm:block h-px flex-1 mx-8 bg-border" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {t.features_items.map((f, i) => {
            const Icon = icons[i] || Radio;
            return (
              <Card key={f.t} className="p-6 hover:border-primary/40 transition-colors bg-card" data-testid={`feature-${i}`}>
                <div className="w-10 h-10 rounded-full grid place-items-center bg-primary/10 ring-1 ring-primary/30 mb-4">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div className="font-serif text-xl mb-1">{f.t}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.d}</p>
              </Card>
            );
          })}
        </div>
      </section>
    </div>
  );
}
